import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../api/reports';
import { toErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useStores } from '../auth/StoreContext';
import { Card, Kpi, PageHead } from '../components/ui';
import { StoreSelector } from '../components/StoreSelector';
import {
  DateRangePicker,
  computeRange,
  type DateRange,
} from '../components/DateRangePicker';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { downloadCsv } from '../lib/download';
import { count, money } from '../lib/format';

type Tab = 'product' | 'category' | 'payment' | 'tax';

const TABS: { key: Tab; label: string }[] = [
  { key: 'product', label: 'Product Sales' },
  { key: 'category', label: 'Category Sales' },
  { key: 'payment', label: 'Payment' },
  { key: 'tax', label: 'Tax' },
];

export function AnalyticsReportPage() {
  const { isSuperAdmin, user } = useAuth();
  const { selectedStoreId } = useStores();
  const [tab, setTab] = useState<Tab>('product');
  const [range, setRange] = useState<DateRange>(() => computeRange('month'));
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const storeId = isSuperAdmin ? selectedStoreId : user?.storeId ?? null;
  // The report endpoints take plain dates (YYYY-MM-DD) and expand each to
  // start/end-of-day server-side. Sending a datetime string ("...T00:00:00")
  // fails backend validation, so pass the raw date strings.
  const from = range.from;
  const to = range.to;
  const rq = { from, to, storeId };

  async function onExport(path: string, filePrefix: string) {
    setExporting(true);
    setExportError(null);
    try {
      await downloadCsv(
        path,
        { from, to, storeId: storeId ?? undefined },
        `${filePrefix}_${range.from}_${range.to}.csv`,
      );
    } catch (err) {
      setExportError(toErrorMessage(err));
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <PageHead
        title="Analytics Reports"
        subtitle="Product, category, payment and tax breakdowns."
        actions={
          <button className="btn btn-secondary" onClick={() => window.print()}>
            🖨️ Print
          </button>
        }
      />

      <div className="toolbar no-print">
        <div className="pill-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={tab === t.key ? 'active' : ''}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="spacer" />
        {isSuperAdmin && <StoreSelector allowAll label="Store" />}
      </div>

      <div className="toolbar no-print">
        <DateRangePicker value={range} onChange={setRange} />
      </div>

      {exportError && <div className="form-error">{exportError}</div>}

      <div className="print-area">
        <div className="hint" style={{ marginBottom: 10 }}>
          {range.from} → {range.to}
        </div>

        {tab === 'product' && (
          <ProductTab
            rq={rq}
            exporting={exporting}
            onExport={() =>
              onExport('/reports/product-wise/export', 'product-wise')
            }
          />
        )}
        {tab === 'category' && <CategoryTab rq={rq} />}
        {tab === 'payment' && (
          <PaymentTab
            rq={rq}
            exporting={exporting}
            onExport={() => onExport('/reports/payment/export', 'payment')}
          />
        )}
        {tab === 'tax' && <TaxTab rq={rq} />}
      </div>
    </>
  );
}

interface RQ {
  from: string;
  to: string;
  storeId: number | null;
}

function ProductTab({
  rq,
  exporting,
  onExport,
}: {
  rq: RQ;
  exporting: boolean;
  onExport: () => void;
}) {
  const q = useQuery({
    queryKey: ['reports', 'product-wise', rq],
    queryFn: () => reportsApi.productWise(rq),
  });

  return (
    <Card
      title="Product Sales"
      actions={
        <button
          className="btn btn-primary btn-sm no-print"
          onClick={onExport}
          disabled={exporting}
        >
          {exporting ? 'Exporting…' : '⬇ Export CSV'}
        </button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />
      ) : !q.data || q.data.length === 0 ? (
        <Empty icon="🧾" title="No product sales in this period" />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Product</th>
                <th className="num">Qty Sold</th>
                <th className="num">Gross Revenue</th>
              </tr>
            </thead>
            <tbody>
              {[...q.data]
                .sort((a, b) => b.grossRevenue - a.grossRevenue)
                .map((r) => (
                  <tr key={r.productId}>
                    <td>{r.productName}</td>
                    <td className="num">{count(r.quantitySold)}</td>
                    <td className="num">{money(r.grossRevenue)}</td>
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="num">
                  {count(q.data.reduce((s, r) => s + r.quantitySold, 0))}
                </td>
                <td className="num">
                  {money(q.data.reduce((s, r) => s + r.grossRevenue, 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}

function CategoryTab({ rq }: { rq: RQ }) {
  const q = useQuery({
    queryKey: ['reports', 'category-wise', rq],
    queryFn: () => reportsApi.categoryWise(rq),
  });

  return (
    <Card title="Category Sales">
      {q.isLoading ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />
      ) : !q.data || q.data.length === 0 ? (
        <Empty icon="🗂️" title="No category sales in this period" />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Category</th>
                <th className="num">Qty Sold</th>
                <th className="num">Gross Revenue</th>
              </tr>
            </thead>
            <tbody>
              {[...q.data]
                .sort((a, b) => b.grossRevenue - a.grossRevenue)
                .map((r) => (
                  <tr key={r.category}>
                    <td>{r.category}</td>
                    <td className="num">{count(r.quantitySold)}</td>
                    <td className="num">{money(r.grossRevenue)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function PaymentTab({
  rq,
  exporting,
  onExport,
}: {
  rq: RQ;
  exporting: boolean;
  onExport: () => void;
}) {
  const q = useQuery({
    queryKey: ['reports', 'payment', rq],
    queryFn: () => reportsApi.payment(rq),
  });

  return (
    <Card
      title="Payment Breakdown"
      actions={
        <button
          className="btn btn-primary btn-sm no-print"
          onClick={onExport}
          disabled={exporting}
        >
          {exporting ? 'Exporting…' : '⬇ Export CSV'}
        </button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />
      ) : !q.data || q.data.length === 0 ? (
        <Empty icon="💳" title="No payments in this period" />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Method</th>
                <th className="num">Transactions</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((r) => (
                <tr key={r.method}>
                  <td>{r.method}</td>
                  <td className="num">{count(r.count)}</td>
                  <td className="num">{money(r.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="num">
                  {count(q.data.reduce((s, r) => s + r.count, 0))}
                </td>
                <td className="num">
                  {money(q.data.reduce((s, r) => s + r.amount, 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}

function TaxTab({ rq }: { rq: RQ }) {
  const q = useQuery({
    queryKey: ['reports', 'tax', rq],
    queryFn: () => reportsApi.tax(rq),
  });

  if (q.isLoading) return <Loading />;
  if (q.isError)
    return <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />;
  if (!q.data) return <Empty icon="🧮" title="No tax data in this period" />;

  return (
    <div className="stack">
      <div className="kpi-grid">
        <Kpi label="Taxable Sales" value={money(q.data.taxableSales)} accent />
        <Kpi label="CGST" value={money(q.data.cgst)} />
        <Kpi label="SGST" value={money(q.data.sgst)} />
        <Kpi label="Total GST" value={money(q.data.totalGst)} />
      </div>
      <Card title="GST Summary">
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Component</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Taxable Sales</td>
                <td className="num">{money(q.data.taxableSales)}</td>
              </tr>
              <tr>
                <td>CGST</td>
                <td className="num">{money(q.data.cgst)}</td>
              </tr>
              <tr>
                <td>SGST</td>
                <td className="num">{money(q.data.sgst)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Total GST</td>
                <td className="num">{money(q.data.totalGst)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
    </div>
  );
}
